var FIGURE = FIGURE || {};

// The header leaves on the way down the page and comes back on the way up.
//
// Written here rather than turned back on: headroom.js is still in the bundle — OffsetBaseResources'
// site.js initialises it on <body> unconditionally, and theme.js destroys it 150ms later — but its
// class names are shared with OBR's own stylesheet, which has four rule blocks keyed off
// .headroom--top / --not-top / --pinned / --unpinned describing a header this is not: a fixed,
// opaque, 100px bar that swaps a light logo for a dark one and hides by moving `top` to -100px.
// Reviving headroom re-arms all of that as a side effect, and the work becomes writing overrides to
// undo it. So the state lives in two classes on .site-header instead, where nothing else is
// listening, and theme.js keeps its kill switch.
//
// Everything that reacts is inside the header, so header-scoped classes are enough:
//   .is-scrolled  the page is off the top — the blur band fades in
//   .is-hidden    gone, translated up past its own top edge
FIGURE.siteHeader = function () {
    var header = document.querySelector('.site-header');
    if (!header) return;

    // Slack in each direction before a direction change counts. iOS reports a stream of small
    // deltas as the URL bar collapses and as momentum runs out, and without this the header flaps.
    // Asymmetric on purpose: coming back should feel immediate, leaving should not be provoked by a
    // twitch.
    var UP_TOLERANCE = 4;
    var DOWN_TOLERANCE = 8;

    // Used only if the header somehow measures zero — a webfont mid-swap, display: none inherited
    // from something unexpected. The header's real height at the narrow breakpoint.
    var FALLBACK_HEIGHT = 91;

    // How far down the page before any of this engages. The header's own extent, so the blur arrives
    // as soon as the page is off the top and not before — this is the one knob for that, and raising
    // it (to a fraction of the viewport, say) would keep the hero pages clean for longer.
    var offset = FALLBACK_HEIGHT;

    var lastY = currentY();
    var hidden = false;

    // Clamped at zero: iOS reports a negative offset while rubber-banding at the top and overshoots
    // past the end at the bottom, and either one reads as a direction change it isn't.
    function currentY() {
        return Math.max(0, window.scrollY || window.pageYOffset || 0);
    }

    // Nothing moves while something is open over the page.
    //
    // The submenus are anchor-positioned to their own nav item, so leaving with one open takes the
    // menu up the screen with it. The two dialogs — the mobile nav and the estate maps — sit over a
    // translucent backdrop the header would be visibly sliding around behind. And the header cannot
    // leave while it holds focus, or a keyboard tab lands on a link that is off-screen.
    //
    // Read off aria-expanded and [open] rather than :popover-open and a list of known selectors, so
    // that anything added later is covered without this being touched — and so an older engine
    // without :popover-open support cannot throw on the selector and take the whole handler down.
    function frozen() {
        if (header.querySelector('[aria-haspopup][aria-expanded="true"]')) return true;
        if (document.querySelector('dialog[open]')) return true;
        // A bootstrap modal pins the page (modal-scroll-lock.js), which reads as a jump to the top.
        if (document.body.classList.contains('modal-open')) return true;
        return header.matches(':focus-within');
    }

    function update() {
        var y = currentY();

        // Frozen means frozen: not the travel, and not the blur either. Tested first, above the
        // at-top case, because "the page is near the top" would otherwise be enough to bring the
        // header sliding in behind an open dialog's backdrop. The reference still follows the page,
        // so releasing compares against where the page is now rather than where it was when the
        // dialog opened.
        if (frozen()) {
            lastY = y;
            return;
        }

        var atTop = y <= offset;
        var delta = y - lastY;

        if (atTop) {
            hidden = false;
            lastY = y;
        } else if (delta > DOWN_TOLERANCE) {
            hidden = true;
            lastY = y;
        } else if (delta < -UP_TOLERANCE) {
            hidden = false;
            lastY = y;
        }
        // A delta inside the tolerance deliberately leaves lastY alone, so a slow drag accumulates
        // until it crosses. Moving the reference every event instead would mean a scroll gentle
        // enough never triggered at all.

        header.classList.toggle('is-scrolled', !atTop);
        header.classList.toggle('is-hidden', hidden);
    }

    // One update per frame at most — a scroll fires far more often than it paints, and the answer
    // cannot change between two paints of the same frame. Same shape as notes-back.js.
    var queued = false;

    function onScroll() {
        if (queued) return;
        queued = true;
        window.requestAnimationFrame(function () {
            queued = false;
            update();
        });
    }

    // Focus arriving brings the header back at once. frozen() stops it leaving while focus is
    // inside, but that only holds from the next scroll event onward — a Tab into an already-hidden
    // header would otherwise land on a link nobody can see, and no scroll follows to fix it.
    header.addEventListener('focusin', function () {
        hidden = false;
        header.classList.remove('is-hidden');
    });

    // Where the bottom of the header actually falls, published for the stylesheet to build on: the
    // flow the four ex-static pages hand back, how far the header travels to get off-screen, and
    // where the notes category back-link parks are all this number plus something.
    //
    // Measured rather than derived because two of its terms are invisible from the stylesheet: the
    // 10px margin-top OffsetBaseResources puts on .site-logo, and the 40px .promo-open pushes the
    // header down by on every page but the four that refuse it. getComputedStyle().top rather than a
    // rect, because a rect includes the translate that hides the header and would then feed its own
    // travel back into itself.
    function publishExtent() {
        var top = parseFloat(window.getComputedStyle(header).top) || 0;
        var height = header.offsetHeight || FALLBACK_HEIGHT;
        offset = top + height;
        document.documentElement.style.setProperty('--site-header-bottom', offset + 'px');
    }

    function remeasure() {
        // Crossing the md breakpoint changes the header's height, and with it the point where all of
        // this starts.
        publishExtent();
        update();
    }

    remeasure();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', remeasure);

    // Coming out of a freeze has to be noticed even when nothing scrolls afterwards, or the header
    // sits wherever the overlay left it until the visitor happens to move the page again. Capturing,
    // because neither event bubbles: `toggle` is the popovers, `close` the two dialogs.
    document.addEventListener('toggle', onScroll, true);
    document.addEventListener('close', onScroll, true);
}();
